import { createElement, useState } from 'react';
import Icon from '../components/Icon';

// Tiny client-side sort hook. `accessors` maps a field key → (row) => comparable
// value (string / number / Date / null). Three-state cycle on header clicks:
// unsorted → asc → desc → unsorted. Default state is no sort, so the page lands
// in the same order the data arrived. Hebrew strings sort with locale='he'.
// `initial` is optional and defaults to unsorted, which is what every caller
// but one wants: most of these tables arrive already ordered by the server.
// The revenue report is the exception — its rows come back in category order
// while the CARD that links into it sorts by spend, so the same six rows
// appeared in two different orders one click apart.
export function useSortable(rows, accessors, initial = null) {
  const [sort, setSort] = useState(
    initial && accessors[initial.field]
      ? { field: initial.field, dir: initial.dir === 'desc' ? 'desc' : 'asc' }
      : { field: null, dir: null }
  );

  const toggle = (field) => {
    setSort(prev => {
      if (prev.field !== field)  return { field, dir: 'asc'  };
      if (prev.dir   === 'asc')  return { field, dir: 'desc' };
      return { field: null, dir: null };
    });
  };

  // Direct setter for controls that pick an exact state (the mobile sort
  // sheet) rather than cycling like header clicks. Null field = unsorted.
  const setSortTo = (field, dir) =>
    setSort(field ? { field, dir: dir === 'desc' ? 'desc' : 'asc' } : { field: null, dir: null });

  const sorted = (() => {
    if (!sort.field || !accessors[sort.field]) return rows;
    const get  = accessors[sort.field];
    const sign = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;            // nulls always last
      if (vb == null) return -1;
      if (va instanceof Date && vb instanceof Date) return (va.getTime() - vb.getTime()) * sign;
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb, 'he') * sign;
      return ((+va) - (+vb)) * sign;
    });
  })();

  // Direction indicator to append to the header label. An active column shows
  // its direction at full strength; every other sortable column shows a dimmed
  // ⇅-style glyph, which is the affordance that the column IS sortable.
  const indicator = (field) => {
    const active = sort.field === field && sort.dir;
    return createElement(Icon, {
      name: !active ? 'sort' : (sort.dir === 'asc' ? 'arrow-up' : 'arrow-down'),
      size: 11,
      strokeWidth: 2,
      className: `th-sort-ic${active ? ' is-active' : ''}`,
    });
  };

  // Spread directly onto a <th> to make it sortable. The inline styles are
  // important to override any th cell defaults from the global stylesheet,
  // and pointerEvents:'auto' insures against any sticky-stacking weirdness.
  const headerProps = (field) => ({
    onClick: () => toggle(field),
    style: {
      cursor: 'pointer',
      userSelect: 'none',
      // An INACTIVE sortable header must look exactly like a non-sortable one.
      // This used to force a lighter grey (#888) while plain `thead th` inherited
      // the darker one from CSS, so on any mixed table (most of them) the sortable
      // columns read as a different, washed-out style rather than as the same
      // header row. Both are now --ink-muted, but the rule still stands.
      // `undefined` inherits the sheet's colour instead of overriding it.
      // Only the ACTIVE column differs — that difference is real signal, and
      // the arrow from indicator() carries it too.
      color: sort.field === field ? '#0f3460' : undefined,
      pointerEvents: 'auto',
    },
    title: 'לחץ למיון',
  });

  return { sorted, sort, toggle, setSortTo, indicator, headerProps };
}
